/**
 * Icons of the special-requests dialog (SRDIALOG-001, QA doc docs features/booking-special-requests/website-dialog-design-a.md R-SDA5).
 * The operator's list has no icon field, so the icon is chosen by a word in the lower-case English name; an unknown name gets the
 * generic icon. The `typeId` never decides (ids differ between DEV, TEST and STG). Pure functions; the popup draws the path as an
 * inline decoration (`aria-hidden`), the item name stays the label.
 */
export type SpecialRequestIcon =
  | 'nosmoke'
  | 'smoke'
  | 'twin'
  | 'double'
  | 'cot'
  | 'stairs'
  | 'moon'
  | 'view'
  | 'link'
  | 'clock'
  | 'celebration'
  | 'generic';

/** The first rule whose word is found wins, so "non-smoking" is listed before "smoking". */
const RULES: readonly { words: readonly string[]; icon: SpecialRequestIcon }[] = [
  { words: ['non-smok', 'nonsmok', 'no smok', 'non smok'], icon: 'nosmoke' },
  { words: ['smok'], icon: 'smoke' },
  { words: ['twin', 'single bed'], icon: 'twin' },
  { words: ['double', 'king', 'queen'], icon: 'double' },
  { words: ['cot', 'baby', 'crib', 'infant'], icon: 'cot' },
  { words: ['high', 'floor', 'upper'], icon: 'stairs' },
  { words: ['quiet'], icon: 'moon' },
  { words: ['view', 'sea', 'pool'], icon: 'view' },
  { words: ['connect', 'adjacent', 'next to'], icon: 'link' },
  { words: ['early', 'late'], icon: 'clock' },
  { words: ['honeymoon', 'birthday', 'anniversary'], icon: 'celebration' }
];

/** The icon of one request item, from its English name only. */
export const requestIconFor = (nameEn: string | null | undefined): SpecialRequestIcon => {
  const name = (nameEn ?? '').toLowerCase();
  for (const rule of RULES) if (rule.words.some((w) => name.includes(w))) return rule.icon;
  return 'generic';
};

/** 24x24 path data (Material-style outlines drawn with `fill="currentColor"`, `fill-rule="evenodd"`). */
export const REQUEST_ICON_PATHS: Readonly<
  Record<SpecialRequestIcon | 'hotel' | 'calendar' | 'bed' | 'info' | 'check', string>
> = {
  nosmoke:
    'M2 6l6.99 7H2v3h9.99l7 7 1.26-1.25-17-17zm18.5 7H22v3h-1.5zM18 13h1.5v3H18zm-.85-8.12c.62-.61 1-1.45 1-2.38h-1.5c0 1.02-.83 1.85-1.85 1.85v1.5c2.24 0 4 1.83 4 4.07V12H20V9.92c0-2.23-1.28-4.15-3.15-5.04zM14.5 8.7h1.53c1.05 0 1.97.74 1.97 2.05V12h1.5v-1.59c0-1.8-1.6-3.16-3.47-3.16H14.5c-1.02 0-1.85-.98-1.85-2s.83-1.75 1.85-1.75V2a3.35 3.35 0 0 0 0 6.7zm2.5 7.23V13h-2.93z',
  smoke:
    'M2 16h15v3H2zm18.5 0H22v3h-1.5zM18 16h1.5v3H18zm.85-8.27c.62-.61 1-1.45 1-2.38C19.85 3.5 18.35 2 16.5 2v1.5c1.02 0 1.85.83 1.85 1.85S17.52 7.2 16.5 7.2v1.5c2.24 0 4 1.83 4 4.07V15H22v-2.24c0-2.22-1.28-4.14-3.15-5.03zm-2.82 2.47H14.5c-1.02 0-1.85-.98-1.85-2s.83-1.75 1.85-1.75v-1.5c-1.85 0-3.35 1.5-3.35 3.35s1.5 3.35 3.35 3.35h1.53c1.05 0 1.97.74 1.97 2.05V15h1.5v-1.64c0-1.81-1.6-3.16-3.47-3.16z',
  twin: 'M1 18v-6c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v6H9v-2H3v2zm2-4h6v-2H3zm10 4v-6c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v6h-2v-2h-6v2zm2-4h6v-2h-6zM1 6h2v2H1zm12 0h2v2h-2z',
  double:
    'M21 10.78V8c0-1.65-1.35-3-3-3h-4c-.77 0-1.47.3-2 .78-.53-.48-1.23-.78-2-.78H6C4.35 5 3 6.35 3 8v2.78c-.61.55-1 1.34-1 2.22v6h2v-2h16v2h2v-6c0-.88-.39-1.67-1-2.22zM14 7h4c.55 0 1 .45 1 1v2h-6V8c0-.55.45-1 1-1zM5 8c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v2H5V8zm-1 7v-2c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v2H4z',
  cot: 'M13 2v8h8c0-4.42-3.58-8-8-8zm6.32 13.89C20.37 14.54 21 12.84 21 11H6.44l-.95-2H2v2h2.22s1.89 4.07 2.12 4.42c-1.1.59-1.84 1.75-1.84 3.08C4.5 20.43 6.07 22 8 22c1.76 0 3.22-1.3 3.46-3h2.08c.24 1.7 1.7 3 3.46 3 1.93 0 3.5-1.57 3.5-3.5 0-1.04-.46-1.97-1.18-2.61zM8 20c-.83 0-1.5-.67-1.5-1.5S7.17 17 8 17s1.5.67 1.5 1.5S8.83 20 8 20zm9 0c-.83 0-1.5-.67-1.5-1.5S16.17 17 17 17s1.5.67 1.5 1.5S17.83 20 17 20z',
  stairs:
    'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 5h-2.5v3.5h-3V15H9v3.5H6v-2h1.5V13h3V9.5h3V6H18v2z',
  moon: 'M12.34 2.02C6.59 1.82 2 6.42 2 12c0 5.52 4.48 10 10 10 3.71 0 6.93-2.02 8.66-5.02-7.51-.25-12.09-8.43-8.32-14.96z',
  view: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  link: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  clock:
    'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  celebration: 'M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  generic:
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  hotel:
    'M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z',
  calendar:
    'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z',
  bed: 'M21 10.78V8c0-1.65-1.35-3-3-3h-4c-.77 0-1.47.3-2 .78-.53-.48-1.23-.78-2-.78H6C4.35 5 3 6.35 3 8v2.78c-.61.55-1 1.34-1 2.22v6h2v-2h16v2h2v-6c0-.88-.39-1.67-1-2.22zM14 7h4c.55 0 1 .45 1 1v2h-6V8c0-.55.45-1 1-1zM5 8c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v2H5V8zm-1 7v-2c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v2H4z',
  info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
  check: 'M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'
};
